// National Rail on the page: the trains (nr.ts) drawn on the shell's canvas,
// the stations that aren't TfL's, their departure boards, the National Rail
// section on a TfL station's card, the line list's rows, hover labels and
// picking. Plugged into the shell (@tol/ui) as its extras.

import type { GeoJSONSource } from 'maplibre-gl';
import * as Core from '@tol/core/core';
import { createClient } from '@tol/core/feed';
import { LOWEST_LINE_LAYER, NR_STATION_LAYER } from '@tol/core/layers';
import * as Render from '@tol/core/render';
import { ZOOM_OFFSET } from '@tol/core/view';
import type { LatLon, ScreenPoint, StationId } from '@tol/core/types';
import { cardTop } from '@tol/ui/card';
import type { StationSection } from '@tol/ui/card';
import { el, esc, lineVars, setLineColor } from '@tol/ui/dom';
import type { Selection, Shell, ShellExtras, StationHit } from '@tol/ui/shell';
import { NR_LINES, createNationalRail, parseBerths, parseDetails, parseHandovers } from './nr.ts';
import type { NationalRail, NrCall, NrDetails } from './nr.ts';
import type { ElizabethRun } from './elizabeth-handover.ts';
import { matchStations, parseBoard, stationFeatures } from './nr-stations.ts';
import type { BoardRow, NrStation } from './nr-stations.ts';
import { loadNrData } from './nr-data-load.ts';
import { loadNrStations } from './nr-station-load.ts';

export interface NrUi {
  readonly hasUid: (uid: string) => boolean;
  readonly ownsElizabeth: (run: ElizabethRun) => boolean;
  readonly extras: ShellExtras;
  // The National Rail section of a TfL station's card, when our operators
  // call there.
  readonly stationSection: (sid: StationId) => StationSection | null;
  // Loads the berths and opens the stream (extras.onMapReady does, on
  // London's page); stop closes it and takes the trains, the track and the
  // stations off the map (the world map, leaving London).
  readonly start: () => void;
  readonly stop: () => void;
}

// Where National Rail runs on a page other than London's own (the world map).
export interface NrUiOptions {
  // London's server, relative to the page: "data/lon/". Default: the page's.
  readonly base?: string;
  // Appended to London's static files (London's page: its deploy's ?v=).
  readonly version?: string;
  // The page's id of a National Rail line ("swr" -> "lon:swr"), and back.
  readonly lineId?: (nrLine: string) => string;
  readonly nrLine?: (pageLine: string) => string;
  // A mapped UID can take over an already-published prediction.
  readonly onMappedUpdate?: () => void;
  readonly showElizabeth?: (uid: string | null, date: string | null, headcode: string) => boolean;
  readonly onDraw?: () => void;
}

const hhmm = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
const fetchFile = (url: string): Promise<Response> => fetch(url, { signal: AbortSignal.timeout(15000) });

function nrLineStyle(lineId: string | null): { readonly name: string; readonly show: string } {
  return (lineId === null ? undefined : NR_LINES[lineId]) ?? { name: 'National Rail', show: '#A3A9C0' };
}

// A National Rail call as a card's stop.
const nrCallItem = (c: NrCall, cls: string): string =>
  `<li${cls}><span>${esc(c.name)}</span><span class="eta">${c.time === null ? '' : hhmm.format(new Date(c.time))}</span></li>`;

export function createNrUi(shell: Shell, options: NrUiOptions = {}): NrUi {
  const { map, state } = shell;
  const base = options.base ?? '';
  const api = createClient({ base });
  const version = options.version ?? shell.version;
  const pageLine = options.lineId ?? ((id: string): string => id);
  const nrLine = options.nrLine ?? ((id: string): string => id);
  // The panel's hidden lines and hovered line, in National Rail's ids.
  const hiddenNr = (): ReadonlySet<string> =>
    options.nrLine ? new Set([...state.hidden].map((id) => nrLine(id))) : state.hidden;
  const focusNr = (): string | null => (state.focus === null ? null : nrLine(state.focus));
  let nationalRail: NationalRail | null = null;
  let mappedFresh = false;
  let mappedMessageAt = 0;
  // Bumped by stop: a start still loading when London is left doesn't open.
  let run = 0;

  // ---- the trains

  async function start(): Promise<void> {
    const mine = ++run;
    // The stations don't wait for the trains' files.
    void loadStations(mine);
    try {
      const loaded = await loadNrData(
        fetchFile,
        base + 'nr-berths.json' + version,
        base + 'nr-track.json' + version,
        () => mine === run,
      );
      if (!loaded || mine !== run || nationalRail) return;
      const { berths: data, track } = loaded;
      const changed = (): void => {
        // The model's event listener processes this same event first.
        queueMicrotask(() => {
          if (mine === run) options.onMappedUpdate?.();
        });
      };
      nationalRail = createNationalRail({
        map: {
          addSource: (id, source) => map.addSource(id, source),
          addLayer: (layer, before) => map.addLayer(layer, before),
          project: ([lon, lat]) => map.project([lon, lat]),
        },
        berths: parseBerths(data),
        handovers: parseHandovers(data),
        track,
        streamUrl: base + 'nr/td/stream',
        connect: (url) => {
          const source = new EventSource(url);
          source.addEventListener('snapshot', () => {
            mappedFresh = true;
            mappedMessageAt = Date.now();
            changed();
          });
          ['step', 'interpose', 'cancel', 'heartbeat'].forEach((name) => {
            source.addEventListener(name, () => {
              if (!mappedFresh) return;
              mappedMessageAt = Date.now();
              if (name !== 'heartbeat') changed();
            });
          });
          source.addEventListener('error', () => {
            mappedFresh = false;
            changed();
          });
          return source;
        },
        beforeLayer: LOWEST_LINE_LAYER,
        ink: Render.INK,
        hidden: hiddenNr,
        focus: focusNr,
        ...(options.showElizabeth ? { showElizabeth: options.showElizabeth } : {}),
      });
      shell.renderRoster();
    } catch (error) {
      console.warn('National Rail trains unavailable', error);
    }
  }

  // ---- the stations: all of them (nr-stations.json), the ones with trains
  // today (/nr/stations; null: unknown, show all), and which are the same
  // place as a TfL station (CRS <-> TfL id), whose card shows both.

  let nrStations: readonly NrStation[] = [];
  let nrServed: ReadonlySet<string> | null = null;
  let tflOfNr = new Map<string, readonly StationId[]>();
  let nrOfTfl = new Map<StationId, string>();
  async function loadStations(mine: number): Promise<void> {
    try {
      const loaded = await loadNrStations(
        fetchFile,
        base + 'nr-stations.json' + version,
        base + 'nr/stations',
        () => mine === run,
      );
      if (!loaded) return;
      nrStations = loaded.stations;
      nrServed = loaded.served;
      updateStations(state.visibleStations);
    } catch (error) {
      console.warn('National Rail stations unavailable', error);
    }
  }
  // Matches stations to TfL's once the network is in, and draws the ones
  // that aren't TfL stations (or whose TfL station is hidden with its lines).
  function updateStations(visible: ReadonlySet<StationId>): void {
    const net = state.net;
    if (nrStations.length === 0) return;
    if (net && tflOfNr.size === 0) {
      tflOfNr = matchStations(nrStations, [...net.stations.values()], Core.dist);
      nrOfTfl = new Map();
      for (const [crs, ids] of tflOfNr) for (const id of ids) nrOfTfl.set(id, crs);
    }
    const own = (s: NrStation): boolean => {
      const tfl = tflOfNr.get(s.crs) ?? [];
      return (nrServed === null || nrServed.has(s.crs)) && !tfl.some((id) => visible.has(id));
    };
    void map.getSource<GeoJSONSource>(NR_STATION_LAYER)?.setData(stationFeatures(nrStations, own));
  }

  // A National Rail station's dot at a point.
  function stationAt(p: ScreenPoint, r: number): StationHit | null {
    if (!map.getLayer(NR_STATION_LAYER)) return null;
    const [f] = map.queryRenderedFeatures(
      [
        [p.x - r, p.y - r],
        [p.x + r, p.y + r],
      ],
      { layers: [NR_STATION_LAYER] },
    );
    const props: Readonly<Record<string, unknown>> | undefined = f?.properties;
    const crs = props?.['crs'];
    const st = typeof crs === 'string' ? nrStations.find((s) => s.crs === crs) : undefined;
    return st
      ? { id: `nr:${st.crs}`, name: st.name, lngLat: [st.lon, st.lat], select: { type: 'nr-station', id: st.crs } }
      : null;
  }

  // ---- a train's card: its service details from /nr/trains, refetched
  // every 20 s while the card is open.

  let nrDetails: { readonly id: string; readonly at: number; readonly details: NrDetails | null } | null = null;
  let nrFetching: { readonly id: string; readonly abort: AbortController } | null = null;
  async function fetchDetails(id: string): Promise<void> {
    const info = nationalRail?.info(id);
    if (!info || nrFetching?.id === id) return;
    nrFetching?.abort.abort();
    const request = { id, abort: new AbortController() };
    nrFetching = request;
    try {
      const data = await api.getJSON('nr/trains?areas=' + encodeURIComponent(info.area), request.abort.signal);
      if (nrFetching !== request) return;
      nrDetails = { id, at: Date.now(), details: parseDetails(data, info.area, info.berth, info.descr) };
    } catch {
      if (nrFetching !== request) return;
      nrDetails = { id, at: Date.now(), details: null };
    } finally {
      if (nrFetching === request) nrFetching = null;
    }
    const sel = state.selected;
    if (sel?.type === 'nr' && sel.id === id) shell.renderCard(false);
  }

  let centeredFor: string | null = null;
  let cancelCenter: (() => void) | null = null;
  // Once per train, when its card opens: the list scrolled so the next stop
  // is in the middle. The sheet is still finding its size when the card
  // opens (fonts, the map's padding), so it's measured again over the first
  // moments and corrected while it's off.
  function centerNext(list: HTMLElement, id: string): void {
    if (centeredFor === id) return;
    centeredFor = id;
    cancelCenter?.();
    const scroller = list.parentElement;
    if (!scroller) return;
    let canceled = false;
    let scrollTop = scroller.scrollTop;
    const gestures = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    const cancel = (): void => {
      canceled = true;
      for (const event of gestures) scroller.removeEventListener(event, cancel);
    };
    cancelCenter = cancel;
    for (const event of gestures) scroller.addEventListener(event, cancel, { passive: true });
    const opened = performance.now();
    const settle = (): void => {
      if (canceled || centeredFor !== id || scroller.scrollTop !== scrollTop) {
        cancel();
        return;
      }
      const at = list.querySelector<HTMLElement>('.next');
      if (at) {
        const row = at.getBoundingClientRect();
        const box = scroller.getBoundingClientRect();
        const off = row.top + row.height / 2 - (box.top + box.height / 2);
        if (row.height > 0 && box.height > 0 && Math.abs(off) > 4) scroller.scrollTop += off;
        scrollTop = scroller.scrollTop;
      }
      if (performance.now() - opened < 800) requestAnimationFrame(settle);
      else cancel();
    };
    requestAnimationFrame(settle);
  }

  function renderTrainCard(card: HTMLElement, id: string, full: boolean): boolean {
    const info = nationalRail?.info(id);
    if (!info) return false;
    const l = nrLineStyle(info.tag?.lineId ?? null);
    setLineColor(card, l.show);
    card.classList.add('trip-card');
    if (!nrDetails || nrDetails.id !== id || Date.now() - nrDetails.at > (nrDetails.details ? 20000 : 3000))
      void fetchDetails(id);
    const d = nrDetails?.id === id ? nrDetails.details : null;
    if (full) {
      centeredFor = null;
      card.innerHTML = `
        ${cardTop('<span data-op></span>', '<button class="btn small" data-follow aria-pressed="false">Follow</button>')}
        <h3 data-dest></h3>
        <p class="sub" data-where></p>
        <div class="trip-scroll"><ol class="stops trip" data-stops></ol></div>`;
      el('.close', card).addEventListener('click', shell.closeCard);
      el('[data-follow]', card).addEventListener('click', () => {
        shell.setFollowing(!state.follow);
        renderTrainCard(card, id, false);
      });
    }
    el('[data-op]', card).textContent = `${d?.operator ?? l.name} · ${info.descr}`;
    const dest = d?.destination ?? info.tag?.destination ?? null;
    el('[data-dest]', card).textContent = dest === null ? 'Train' : `To ${dest}`;
    const delay = d?.delay ?? info.tag?.delay ?? null;
    const running =
      delay === null ? '' : delay <= 0 ? (delay < 0 ? `${-delay} min early` : 'On time') : `${delay} min late`;
    const from = d?.origin === null || d?.origin === undefined ? '' : `From ${d.origin}`;
    const last = d?.lastPlace === null || d?.lastPlace === undefined ? '' : `Last reported at ${d.lastPlace}`;
    el('[data-where]', card).textContent = [from, running, last].filter((s) => s !== '').join('. ');
    // Every call: those made (muted), the next one, then the rest. The times
    // are the timetable's.
    const next = d?.next ?? [];
    const stops =
      (d?.past ?? []).map((c) => nrCallItem(c, ' class="past"')).join('') +
      next.map((c, i) => nrCallItem(c, i === 0 ? ' class="next"' : '')).join('');
    const list = el('[data-stops]', card);
    const empty = d
      ? 'No more stops'
      : nrDetails?.id === id
        ? 'Train details unavailable. Retrying…'
        : 'Looking up this train…';
    list.innerHTML = stops === '' ? `<li><span>${empty}</span></li>` : stops;
    if (d) centerNext(list, id);
    const fb = el('[data-follow]', card);
    fb.setAttribute('aria-pressed', String(state.follow));
    fb.textContent = state.follow ? 'Following' : 'Follow';
    return true;
  }

  // ---- departure boards for National Rail stations, by CRS: refetched
  // every 30 s while a card shows one.

  const boards = new Map<string, { readonly at: number; readonly rows: readonly BoardRow[] | null }>();
  const boardFetching = new Set<string>();
  async function fetchBoard(crs: string): Promise<void> {
    if (boardFetching.has(crs)) return;
    boardFetching.add(crs);
    try {
      const data = await api.getJSON('nr/station?crs=' + encodeURIComponent(crs));
      boards.set(crs, { at: Date.now(), rows: parseBoard(data, crs) });
    } catch {
      boards.set(crs, { at: Date.now(), rows: null });
    } finally {
      boardFetching.delete(crs);
    }
    if (state.selected) shell.renderCard(false);
  }

  // A station's board as list items. A train that's on the map can be tapped.
  function boardRows(crs: string, max = 8): string {
    const b = boards.get(crs);
    if (!b || Date.now() - b.at > 30000) void fetchBoard(crs);
    if (!b) return '<li><span></span><span class="muted">Looking up trains…</span></li>';
    if (!b.rows) return '<li><span></span><span class="muted">Train times unavailable</span></li>';
    const rows = b.rows.filter((r) => r.lineId === null || !state.hidden.has(pageLine(r.lineId))).slice(0, max);
    if (rows.length === 0) return '<li><span></span><span class="muted">No trains in the next two hours</span></li>';
    return rows
      .map((r) => {
        const l = nrLineStyle(r.lineId);
        const onMap = nationalRail?.byUid(r.uid) ?? null;
        const status = r.cancelled
          ? 'Cancelled'
          : r.delayed
            ? 'Delayed'
            : r.expected === null
              ? ''
              : r.expected <= r.time
                ? 'On time'
                : `Expected ${hhmm.format(new Date(r.expected))}`;
        const plat = r.platform === null ? '' : `Platform ${r.platform}`;
        const what = r.terminates ? `From ${r.origin}` : r.destination;
        const detail = [r.operator, plat, status].filter((s) => s !== '').join(', ');
        return `<li style="${lineVars(l.show)}"${onMap === null ? '' : ` class="live" data-nr="${esc(onMap)}" title="Show this train" role="button" tabindex="0"`}><span class="bar"></span><span>${esc(what)}</span><span class="eta">${hhmm.format(new Date(r.time))}</span>
          <span class="plat">${esc(detail)}</span>${r.reason === null ? '' : `<span class="plat reason">${esc(r.reason)}</span>`}</li>`;
      })
      .join('');
  }
  function boardClicks(list: HTMLElement): void {
    // A row on the map is a button to a keyboard too (Enter or Space).
    list.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const li = e.target instanceof HTMLElement && e.target.matches('[data-nr]') ? e.target : null;
      if (!li) return;
      e.preventDefault();
      li.click();
    });
    list.addEventListener('click', (e) => {
      const li = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-nr]') : null;
      const train = li?.dataset['nr'];
      const info = train === undefined ? null : nationalRail?.info(train);
      if (!li || train === undefined) return;
      if (!info) {
        li.classList.remove('live');
        for (const attribute of ['data-nr', 'title', 'role', 'tabindex']) li.removeAttribute(attribute);
        const unavailable = document.createElement('span');
        unavailable.className = 'plat';
        unavailable.setAttribute('role', 'status');
        unavailable.textContent = 'Train position unavailable';
        li.append(unavailable);
        return;
      }
      nrDetails = null;
      shell.select({ type: 'nr', id: train });
      map.flyTo({
        center: [info.lon, info.lat],
        zoom: Math.max(map.getZoom(), 13 - ZOOM_OFFSET),
        duration: 800,
        animate: !shell.reduceMotion,
        essential: true,
      });
    });
  }

  function renderStationCard(card: HTMLElement, crs: string, full: boolean): boolean {
    const st = nrStations.find((s) => s.crs === crs);
    if (!st) return false;
    setLineColor(card, '#A3A9C0');
    card.classList.remove('trip-card');
    if (full) {
      card.innerHTML = `
        ${cardTop('National Rail')}
        <h3>${esc(st.name)}</h3>
        <ul class="arrivals" data-board></ul>`;
      el('.close', card).addEventListener('click', shell.closeCard);
      boardClicks(el('[data-board]', card));
    }
    el('[data-board]', card).innerHTML = boardRows(crs);
    return true;
  }

  // The National Rail section of a TfL station's card, if our operators
  // call there.
  function stationSection(sid: StationId): StationSection | null {
    const nrCrs = nrOfTfl.get(sid);
    const crs = nrCrs !== undefined && (nrServed === null || nrServed.has(nrCrs)) ? nrCrs : undefined;
    if (crs === undefined) return null;
    return { title: 'National Rail', rows: () => boardRows(crs, 6), wire: boardClicks };
  }

  // National Rail counts change between polls.
  setInterval(() => {
    if (nationalRail) shell.renderRoster();
  }, 5000);

  const extras: ShellExtras = {
    onMapReady: () => void start(),
    draw: (ctx, dt, scale, w, h) => {
      options.onDraw?.();
      nationalRail?.draw(ctx, dt, scale, w, h);
    },
    drawn: () => nationalRail?.drawn() ?? [],
    pick: (pt, radius) => {
      const nr = nationalRail?.pick(pt.x, pt.y, radius) ?? null;
      if (!nr) return null;
      const l = nrLineStyle(nr.train.tag?.lineId ?? null);
      const dest = nr.train.tag?.destination ?? null;
      return {
        select: { type: 'nr', id: nr.train.key },
        distance: nr.distance,
        color: l.show,
        text: dest === null ? l.name : `${l.name} to ${dest}`,
      };
    },
    stationAt,
    renderCard: (card: HTMLElement, sel: Selection, full: boolean): boolean => {
      if (sel.type === 'nr') {
        if (full) nrDetails = null;
        return renderTrainCard(card, sel.id, full);
      }
      if (sel.type === 'nr-station') return renderStationCard(card, sel.id, full);
      return false;
    },
    rosterRows: () =>
      Object.entries(NR_LINES).map(([id, l]: readonly [string, { readonly name: string; readonly show: string }]) => ({
        id: pageLine(id),
        name: l.name,
        show: l.show,
        status: 'National Rail',
      })),
    counts: (within) => {
      if (!mappedFresh || Date.now() - mappedMessageAt > 45_000) return null;
      const n = nationalRail?.counts(within);
      if (!n || !options.lineId) return n ?? null;
      const out = new Map<string, number>();
      for (const [id, c] of n) out.set(pageLine(id), c);
      return out;
    },
    onStationsVisible: updateStations,
    followed: (sel): LatLon | null => {
      if (sel.type !== 'nr') return null;
      const info = nationalRail?.info(sel.id);
      const lineId = info?.tag?.lineId ?? null;
      if (!info || (lineId !== null && state.hidden.has(pageLine(lineId)))) return null;
      return { lat: info.lat, lon: info.lon };
    },
  };
  function stop(): void {
    run++;
    cancelCenter?.();
    centeredFor = null;
    nrFetching?.abort.abort();
    nrFetching = null;
    nrDetails = null;
    nationalRail?.close();
    nationalRail = null;
    mappedFresh = false;
    mappedMessageAt = 0;
    if (map.getLayer('nr-track')) map.removeLayer('nr-track');
    if (map.getSource('nr-track')) map.removeSource('nr-track');
    nrStations = [];
    tflOfNr = new Map();
    nrOfTfl = new Map();
    void map.getSource<GeoJSONSource>(NR_STATION_LAYER)?.setData({ type: 'FeatureCollection', features: [] });
  }

  return {
    extras,
    ownsElizabeth: (identity) =>
      mappedFresh &&
      Date.now() - mappedMessageAt <= 45_000 &&
      (nationalRail?.ownsRun(identity.uid, identity.date, identity.headcode) ?? false),
    // nr.ts also treats three missed 15-second heartbeats as stale. A
    // retained/fading TD object after an error is not replacement coverage.
    hasUid: (uid) =>
      mappedFresh &&
      Date.now() - mappedMessageAt <= 45_000 &&
      nationalRail?.byUid(uid) !== null &&
      nationalRail?.byUid(uid) !== undefined,
    stationSection,
    start: () => {
      void start();
    },
    stop,
  };
}
